import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Observable, Subscription, TimeoutError, interval, throwError } from "rxjs";
import { exhaustMap, filter, map, switchMap, take, tap, timeout } from "rxjs/operators";
import { AiChatServiceService } from "src/app/components/direct/service/ai-chat-service.service";
import {
  AsyncInvokePayload,
  AsyncInvokeResponseBody,
  AsyncResultResponse,
  JobStatusResponseBody,
} from "src/app/components/direct/interface/scraii-ai-interface";
import { SessionService } from "src/app/services/session.service";
import { avatarColor, initialsOf, newMessageId } from "../meeting-room/meeting-utils";

/**
 * Same 3-step async flow as the full Neo screen (scraii-ai.component.ts `sendChatbotMessage`):
 * submit a job, poll its status, then fetch the finished result. The timings match that screen
 * except the overall poll budget, which is shorter here because the user is in a live meeting.
 */
const SUBMIT_TIMEOUT_MS = 60000;
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 180000;
const FETCH_TIMEOUT_MS = 60000;
const PROMPT_MAX_CHARS = 2000;

const JOB_COMPLETED = "COMPLETED";
const JOB_FAILED = "FAILED";

/** Projects whose response is prose in `answer` rather than SQL + rows (mirrors `isAnswerOnlyProject`). */
const ANSWER_ONLY_PROJECTS = ["config_ai", "enrollment_rag", "contract_rag", "data_dictionary", "verification_audit"];

const COPY = {
  generic: "Neo couldn't answer that just now. Please try again.",
  slow: "Neo is taking longer than usual. Try again, or open Neo.ai from the menu for longer questions.",
  noData: "Neo couldn't retrieve the data for this. Try rephrasing your question.",
  noAnswer: "Neo didn't find an answer to that. Try rephrasing your question.",
  noRows: "Neo looked, but found no matching records for that.",
  table: "Neo found results for this — open Neo.ai from the menu to see the table.",
  chart: "Neo built a chart for this — open Neo.ai from the menu to see it.",
  accessDenied: "You don't have access to the data this question needs.",
};

/** Status line shown in Neo's card while a job runs, by elapsed time. */
const PENDING_COPY: [number, string][] = [
  [0, "Understanding what you need…"],
  [12000, "Working through your data…"],
  [40000, "Still working — this one is taking a little longer…"],
];

type NeoState = "pending" | "answer" | "note" | "error";

interface NeoMessage {
  id: string;
  from: "user" | "neo";
  text: string;
  /** Neo replies only. */
  state?: NeoState;
  /** Neo replies only: the question this reply answers, so "Try again" can resend it. */
  prompt?: string;
}

/** Fields the async result carries beyond the shared `AsyncResultResponse` interface. */
interface NeoResult extends Partial<AsyncResultResponse> {
  is_ops_overview?: boolean;
  verification_audit?: { answer?: unknown } | null;
}

/** The subset of sessionStorage "userInformation" the Neo payload needs. */
interface PortalUserInfo {
  user_team?: string | null;
  employee_key?: number | string | null;
  usr_display_nm?: string | null;
}

/** An error whose message was written for the user here, rather than one from the HTTP layer. */
class NeoError extends Error {}

@Component({
  selector: "app-meeting-neo",
  standalone: false,
  templateUrl: "./meeting-neo.component.html",
  styleUrls: ["./meeting-neo.component.scss"],
})
export class MeetingNeoComponent implements OnInit, AfterViewInit, OnDestroy {
  /** The local user's display name — drives the user avatar's initials and colour. */
  @Input() userName = "";
  /** Optional context; not sent to Neo. */
  @Input() meetingTitle = "";

  @ViewChild("thread") thread?: ElementRef<HTMLDivElement>;
  @ViewChild("box") box?: ElementRef<HTMLTextAreaElement>;

  messages: NeoMessage[] = [];
  draft = "";
  /** False when this tab has no portal session (e.g. a guest who opened a join link). */
  available = false;
  readonly maxChars = PROMPT_MAX_CHARS;

  private userId = "";
  private teamName = "";
  private empDimKey: number | null = null;
  private sessionDisplayName = "";
  /** One conversation per panel, so follow-up questions keep their context. */
  private conversationId = "";
  private request?: Subscription;
  private resizeObserver?: ResizeObserver;
  private stickToBottom = true;

  constructor(private aiChat: AiChatServiceService, private session: SessionService) {}

  ngOnInit(): void {
    this.readSession();
    this.conversationId = this.newConversationId();
  }

  ngAfterViewInit(): void {
    const el = this.thread?.nativeElement;
    if (el && typeof ResizeObserver !== "undefined") {
      // The room parks this panel with display:none while it is closed, which drops the scroll
      // position (and an answer that lands meanwhile can't be scrolled to). Re-pin to the newest
      // message whenever the thread is shown again or resized by a breakpoint.
      this.resizeObserver = new ResizeObserver(() => {
        if (this.stickToBottom && el.clientHeight > 0) el.scrollTop = el.scrollHeight;
      });
      this.resizeObserver.observe(el);
    }
    // On touch devices focusing would pop the on-screen keyboard every time the panel opens.
    if (this.available && window.matchMedia("(hover: hover)").matches) {
      setTimeout(() => this.box?.nativeElement.focus());
    }
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.resizeObserver?.disconnect();
  }

  get busy(): boolean {
    return this.messages.some((message) => message.state === "pending");
  }

  get canSend(): boolean {
    const text = this.draft.trim();
    return this.available && !this.busy && !!text && this.draft.length <= this.maxChars;
  }

  get userInitials(): string {
    return initialsOf(this.displayName);
  }

  get userAvatar(): { background: string; color: string } {
    return avatarColor(this.displayName);
  }

  trackMessage(_: number, message: NeoMessage): string {
    return message.id;
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  submit(): void {
    if (!this.canSend) return;
    const prompt = this.draft.trim();
    this.draft = "";
    this.messages.push({ id: newMessageId(), from: "user", text: prompt });
    const reply: NeoMessage = { id: newMessageId(), from: "neo", text: "", prompt };
    this.messages.push(reply);
    this.stickToBottom = true;
    this.ask(reply);
  }

  retry(reply: NeoMessage): void {
    if (this.busy || !reply.prompt) return;
    this.stickToBottom = true;
    this.ask(reply);
  }

  onScroll(): void {
    const el = this.thread?.nativeElement;
    // A parked (display:none) thread measures 0; that says nothing about where the user was.
    if (!el || el.clientHeight === 0) return;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  /** Runs one question through the async job flow and writes the outcome into `reply`. */
  private ask(reply: NeoMessage): void {
    this.request?.unsubscribe();
    reply.state = "pending";
    reply.text = PENDING_COPY[0][1];
    this.scrollToEnd();

    const payload: AsyncInvokePayload = {
      conversation_id: this.conversationId,
      prompt: reply.prompt || "",
      user_id: this.userId,
      data: "Y",
      // The panel shows prose only, so always ask for the summary the full screen makes optional.
      summary: "Y",
      team_name: this.teamName,
      emp_dim_key: this.empDimKey,
      brand_key: null,
      page_name: "",
    };
    const startedAt = Date.now();

    this.request = this.runJob(payload, () => {
      reply.text = this.pendingCopy(Date.now() - startedAt);
    }).subscribe({
      next: (result) => {
        this.applyResult(reply, result);
        this.scrollToEnd();
      },
      error: (err: unknown) => {
        reply.state = "error";
        reply.text = this.errorCopy(err);
        this.scrollToEnd();
      },
    });
  }

  /** Submit → poll until COMPLETED/FAILED → fetch the result. One subscription cancels all three. */
  private runJob(payload: AsyncInvokePayload, onPoll: () => void): Observable<NeoResult> {
    return this.aiChat.submitAsyncJob(payload).pipe(
      timeout(SUBMIT_TIMEOUT_MS),
      map((res) => this.parseBody<AsyncInvokeResponseBody>(res?.body)?.jobid || ""),
      switchMap((jobid) => (jobid ? this.pollUntilDone(jobid, onPoll) : throwError(() => new NeoError(COPY.generic)))),
      switchMap((jobid) => this.aiChat.getJobResults(jobid).pipe(timeout(FETCH_TIMEOUT_MS))),
      map((result) => (result || {}) as NeoResult)
    );
  }

  private pollUntilDone(jobid: string, onPoll: () => void): Observable<string> {
    const startedAt = Date.now();
    return interval(POLL_INTERVAL_MS).pipe(
      // exhaustMap, not switchMap: a slow status call is waited for rather than cancelled and re-sent.
      exhaustMap(() =>
        Date.now() - startedAt > POLL_TIMEOUT_MS
          ? throwError(() => new NeoError(COPY.slow))
          : this.aiChat.pollJobStatus(jobid)
      ),
      map((res) => this.parseBody<JobStatusResponseBody>(res?.body)),
      tap(() => onPoll()),
      filter((body) => !body || body.status === JOB_COMPLETED || body.status === JOB_FAILED),
      take(1),
      map((body) => {
        if (!body) throw new NeoError(COPY.generic);
        if (body.status === JOB_FAILED) throw new NeoError(COPY.noData);
        // Same gate as the full screen: a completed job without a stored result has nothing to fetch.
        if (!body.presigned_url) throw new NeoError(COPY.generic);
        return jobid;
      })
    );
  }

  /** Keeps only the answer's prose; charts, tables and SQL stay in the full Neo screen. */
  private applyResult(reply: NeoMessage, res: NeoResult): void {
    if (this.isText(res.conversation_id)) this.conversationId = res.conversation_id;

    const set = (state: NeoState, text: string) => {
      reply.state = state;
      reply.text = text;
    };

    if (res.access_denied === true) {
      // The full screen shows this backend message verbatim; it is an access notice, not data.
      set("error", this.isText(res.error) ? res.error.trim() : COPY.accessDenied);
      return;
    }

    const audit = this.verificationAuditAnswer(res);
    if (audit) return set("answer", this.plainText(audit));

    // The backend's own error text can carry SQL or table names, so it is never shown.
    if (this.isText(res.error)) return set("error", COPY.noData);

    if (res.needs_clarification && this.isText(res.clarification_question)) {
      return set("answer", this.plainText(res.clarification_question));
    }

    if (res.project && ANSWER_ONLY_PROJECTS.includes(res.project)) {
      const answer = this.isText(res.answer) ? this.plainText(res.answer.replace(/\[Table:\s*[^\]]*\]/g, "")) : "";
      if (answer) return set("answer", answer);
      return set("note", Array.isArray(res.tables) && res.tables.length ? COPY.table : COPY.noAnswer);
    }

    if (res.response_kind === "multi_sql" && Array.isArray(res.sql_queries)) {
      const lines = res.sql_queries
        .filter((query) => this.isText(query.summary))
        .map((query) => (query.label ? `${query.label}: ` : "") + this.plainText(query.summary || ""));
      if (lines.length) return set("answer", lines.join("\n\n"));
      return set("note", COPY.table);
    }

    // Covers SQL answers and the ops overview, both of which put their prose in `summary`.
    if (this.isText(res.summary)) return set("answer", this.plainText(res.summary));

    const rows = Array.isArray(res.result) ? res.result.length : -1;
    if (rows === 0) return set("note", COPY.noRows);
    if (res.chart_type && rows > 0) return set("note", COPY.chart);
    if (rows > 0 || this.isText(res.sql_query) || res.is_ops_overview) return set("note", COPY.table);
    set("note", COPY.noAnswer);
  }

  /** verification_audit.answer, used when the SQL result came back empty (mirrors the full screen). */
  private verificationAuditAnswer(res: NeoResult): string {
    if (res.needs_clarification || res.is_ops_overview || res.response_kind === "multi_sql") return "";
    if (res.project && ANSWER_ONLY_PROJECTS.includes(res.project)) return "";
    const empty = res.result == null || (Array.isArray(res.result) && res.result.length === 0);
    const answer = res.verification_audit?.answer;
    return empty && this.isText(answer) ? answer.trim() : "";
  }

  /**
   * AiChatServiceService rethrows every HTTP failure as a plain `Error` carrying portal copy
   * (friendlyApiErrorMessage), so that message is safe to show. Anything else — a TypeError,
   * a parse failure — gets the generic line rather than its developer-facing text.
   */
  private errorCopy(err: unknown): string {
    if (err instanceof TimeoutError) return COPY.slow;
    if (err instanceof NeoError) return err.message;
    if (err instanceof Error && err.constructor === Error && err.message) return err.message;
    return COPY.generic;
  }

  private pendingCopy(elapsedMs: number): string {
    let text = PENDING_COPY[0][1];
    for (const [after, copy] of PENDING_COPY) {
      if (elapsedMs >= after) text = copy;
    }
    return text;
  }

  /** Light markdown → plain text: the markdown pipe is declared in DirectModule, not SharedModule. */
  private plainText(markdown: string): string {
    return markdown
      .split("\n")
      .map((line) =>
        line.trim() === "---"
          ? ""
          : line
              .replace(/^\s*#{1,6}\s+/, "")
              .replace(/^\s*[-*]\s+/, "• ")
              .replace(/\*\*([^*]+)\*\*/g, "$1")
              .trimEnd()
      )
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }

  private parseBody<T>(body: unknown): T | null {
    if (body && typeof body === "object") return body as T;
    if (typeof body !== "string") return null;
    try {
      return JSON.parse(body) as T;
    } catch {
      return null;
    }
  }

  private isText(value: unknown): value is string {
    return typeof value === "string" && value.trim().length > 0;
  }

  private get displayName(): string {
    return this.userName || this.sessionDisplayName;
  }

  /**
   * The payload fields come from the same places the full Neo screen reads them:
   * user_id from SessionService.getUsername(), team and employee key from sessionStorage
   * "userInformation" (written at sign-in by home.component.ts).
   */
  private readSession(): void {
    let info: PortalUserInfo | null = null;
    try {
      info = JSON.parse(sessionStorage.getItem("userInformation") || "null") as PortalUserInfo | null;
    } catch {
      info = null;
    }
    const userId: unknown = this.session.getUsername();
    this.userId = typeof userId === "string" ? userId : "";
    this.available = !!info && !!this.userId;
    if (!info) return;

    this.teamName = (info.user_team ?? "ops").toLowerCase();
    const key = info.employee_key;
    this.empDimKey = typeof key === "number" ? key : key != null && key !== "" && !isNaN(Number(key)) ? Number(key) : null;
    this.sessionDisplayName = info.usr_display_nm || "";
  }

  private newConversationId(): string {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : newMessageId() + newMessageId();
  }

  private scrollToEnd(): void {
    if (!this.stickToBottom) return;
    setTimeout(() => {
      const el = this.thread?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }
}
